import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { Anchor, Form } from '@anytag/shared-atoms';
import { getSocialMediaText } from '@anytag/shared-utils';
import { CampaignSocialMediaType } from '@anytag/graphql';
import { DisabledFields, Option } from '@anytag/shared-types';
import { CheckBoxField, RichEditorField, SelectorField, TextField } from '@anytag/shared-molecules';
import { EngagementCampaignFormValues, EngagementCampaignFormKeys } from '../types';

interface CastingInformationProps {
  disabledFields: DisabledFields<EngagementCampaignFormKeys>;
}
export const CastingInformation = ({ disabledFields }: CastingInformationProps) => {
  const { t, i18n } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { watch, setValue } = useFormContext<EngagementCampaignFormValues>();

  const [isTtcmCampaignValue, socialMediasValue] = watch(['isTtcmCampaign', 'socialMedias']);
  const socialMediaOptions: Option[] = [
    CampaignSocialMediaType.FACEBOOK,
    CampaignSocialMediaType.INSTAGRAM,
    CampaignSocialMediaType.INSTAGRAM_STORY,
    CampaignSocialMediaType.TWITTER,
    CampaignSocialMediaType.YOUTUBE,
    CampaignSocialMediaType.TIKTOK,
    CampaignSocialMediaType.DOUYIN,
    CampaignSocialMediaType.THREADS,
    CampaignSocialMediaType.XHS
  ].map((type) => ({ value: type, label: getSocialMediaText(type) }));

  return (
    <>
      <Form.SectionTitle index={2} label={t('Casting Information')} />
      <Form.Fields itemsPerRow={3}>
        <SelectorField<EngagementCampaignFormKeys>
          name="socialMedias"
          help={t('Tooltip.Social Media')}
          title={t('Selector.Social Media')}
          options={socialMediaOptions}
          disabled={disabledFields?.socialMedias}
          onChange={(name, value) => {
            setValue(name, value, { shouldValidate: true });

            if (isAdminStaff && !value.includes(CampaignSocialMediaType.TIKTOK) && isTtcmCampaignValue) {
              setValue('isTtcmCampaign', false);
            }
          }}
          multiple
          required
        />

        <TextField<EngagementCampaignFormKeys>
          type="number"
          placeholder="0"
          name="numberOfInfluencers"
          title={t('TextForm.Number of Influencers')}
          help={t('Tooltip.Number of influencers Help')}
          disabled={disabledFields?.numberOfInfluencers}
        />
      </Form.Fields>

      {isAdminStaff && socialMediasValue.includes(CampaignSocialMediaType.TIKTOK) ? (
        <Form.Field>
          <CheckBoxField<EngagementCampaignFormKeys>
            className="p-0"
            name="isTtcmCampaign"
            disabled={disabledFields?.isTtcmCampaign}
            label={t('Run this campaign as a TikTok Creator Marketplace (TTCM) campaign')}
          />
          <Anchor
            variant="blue"
            target="_blank"
            rel="noopener noreferrer"
            label={t('How to run TTCM campaigns?')}
            className="mt-1.5 inline-block text-base"
            href={
              i18n.language === 'ja'
                ? 'https://anymind-group.gitbook.io/anytag-1/kyanpn/tiktok-creator-marketplacettcm-kyanpn'
                : 'https://anymind-group.gitbook.io/english-1/kyanpn/tiktok-creator-marketplace-ttcm-campaign'
            }
          />
        </Form.Field>
      ) : null}

      <RichEditorField<EngagementCampaignFormKeys>
        name="defaultPostRequirement"
        help={t('Tooltip.Post Requirements Help')}
        title={t('TextForm.Post Requirements')}
        disabled={disabledFields?.defaultPostRequirement}
        placeholder={t<string>('TextForm.Post Requirements')}
      />
    </>
  );
};
